import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { TouchButton } from '../TouchButton';
import type { ModalProps } from './Modal.types';

export const Modal = ({
  title,
  isOpen,
  onClose,
  children,
  primaryButtonTitle,
  onPrimaryButtonClick,
  primaryButtonSlot,
  primaryButtonProps,
  onCancel,
  cancelButtonTitle,
  cancelButtonAlign = 'right',
  secondaryButtonTitle,
  onSecondaryButtonClick,
  secondaryButtonAlign,
  titleAction,
  hideActions = false,
  maxWidth = 'sm',
  fullWidth = true,
  isMobile = false,
  'data-testid': dataTestId,
  dialogProps,
}: ModalProps) => {
  const { t } = useTranslation('modal');
  const { open: _open, onClose: _onClose, PaperProps: paperProps, ...safeDialogProps } = dialogProps ?? {};
  void _open;
  void _onClose;

  const hasPrimaryButton = Boolean(primaryButtonSlot || primaryButtonTitle || onPrimaryButtonClick);
  const hasCancelButton = Boolean(onCancel);

  const showCancelOnLeft = hasCancelButton && cancelButtonAlign === 'left';
  const showCancelOnRight = hasCancelButton && cancelButtonAlign === 'right';
  const resolvedSecondaryButtonAlign = secondaryButtonAlign ?? (cancelButtonAlign === 'right' ? 'left' : 'right');
  const showSecondaryButtonOnLeft = Boolean(onSecondaryButtonClick) && resolvedSecondaryButtonAlign === 'left';
  const showSecondaryButtonOnRight = Boolean(onSecondaryButtonClick) && resolvedSecondaryButtonAlign === 'right';
  const showLeftGroup = showCancelOnLeft || showSecondaryButtonOnLeft;
  const showRightGroup = showSecondaryButtonOnRight || showCancelOnRight || hasPrimaryButton;
  const showActions = !hideActions && (showLeftGroup || showRightGroup);

  return (
    <Dialog
      data-testid={dataTestId}
      open={isOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      scroll={isMobile ? 'body' : undefined}
      PaperProps={
        isMobile
          ? { ...paperProps, style: { margin: 16, width: 'calc(100% - 32px)', maxWidth: 'none', ...paperProps?.style } }
          : paperProps
      }
      {...safeDialogProps}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {title}
          {titleAction}
        </Stack>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {showActions && (
        <DialogActions
          disableSpacing={isMobile}
          sx={{ justifyContent: 'space-between', ...(isMobile && { flexWrap: 'wrap', gap: 2 }) }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : undefined }}>
            {showCancelOnLeft && onCancel && (
              <TouchButton data-testid="modal-cancel-button" onClick={onCancel}>
                {cancelButtonTitle || t('cancel')}
              </TouchButton>
            )}
            {showSecondaryButtonOnLeft && onSecondaryButtonClick && (
              <TouchButton data-testid="modal-secondary-button" onClick={onSecondaryButtonClick}>
                {secondaryButtonTitle || t('back')}
              </TouchButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : undefined }}>
            {showSecondaryButtonOnRight && onSecondaryButtonClick && (
              <TouchButton data-testid="modal-secondary-button" onClick={onSecondaryButtonClick}>
                {secondaryButtonTitle || t('back')}
              </TouchButton>
            )}
            {showCancelOnRight && onCancel && (
              <TouchButton data-testid="modal-cancel-button" onClick={onCancel}>
                {cancelButtonTitle || t('cancel')}
              </TouchButton>
            )}
            {hasPrimaryButton &&
              (primaryButtonSlot || (
                <TouchButton
                  data-testid="modal-primary-button"
                  variant="contained"
                  onClick={onPrimaryButtonClick}
                  {...primaryButtonProps}
                >
                  {primaryButtonTitle || t('save')}
                </TouchButton>
              ))}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};
